import { useState, useEffect, useRef } from "react";
import arrayUtilities from "../../utilities/arrayUtilities";
import stringHelpers from "../../utilities/stringHelpers";
import Api from "../api/api";
import { ReportsCharts } from "./reportsCharts";
import { dateRange, toDateString } from "../../utilities/dateUtilities";
import { reportDatePresetByLabel } from "./reportsEnums";
import { saveAs } from 'file-saver';
import { EmptyState } from "../dashboard/emptyState";
import Loader from "../shared/loader";
import { Link } from "react-router-dom/cjs/react-router-dom";
import { FiltersModalTrigger } from "../shared/filtersModalTrigger";
import { AppliedFilterPill, renderTagFilterItemLabel } from "../shared/appliedFilterPill";
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities";
import { AppealsReportFiltersModal } from "./filters-modals/appealsReportFiltersModal";
import { downloadReportPdf } from "./downloadReportPdf";
import { PrintPdfHeader } from "./printPdfHeader";
import { AppealsScheduleModal } from "./schedule-modals/appealsScheduleModal";
const groupByLabel = (value) => value === 1 ? "Tag" : "Site"

export const AppealsReport = ({ organisationId, organisationName, organisationLogo, users, tags, token }) => {
    const [reportsData, setReportsData] = useState(null);
    const [fromDate, setFromDate] = useState(dateRange("lastMonth").firstDate);
    const [toDate, setToDate] = useState(dateRange("lastMonth").lastDate);
    const [datePreset, setDatePreset] = useState(reportDatePresetByLabel["Last month"]);
    const [selectedUser, setSelectedUser] = useState("")
    const [appealsGroupBy, setAppealsGroupBy] = useState(0)
    const [selectedTags, setSelectedTags] = useState([])
    const [columnTotals, setColumnTotals] = useState(null);

    const [chartData, setChartData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [showScheduleExportModal, setShowScheduleExportModal] = useState(false);
    const pdfRef = useRef()

    const pdfUserName = (userId) => {
        if (!stringHelpers.isNullOrEmpty(userId)) {
            const user = users.find(item => item.id === userId)
            if (user !== undefined) {
                return `${ user.firstName } ${ user.lastName }`
            }
        } else {
            return "All"
        }
    }

    const handleResetFilters = () => {
        const defaultRange = dateRange("lastMonth");
        setFromDate(defaultRange.firstDate);
        setToDate(defaultRange.lastDate);
        setDatePreset(reportDatePresetByLabel["Last month"]);
        setSelectedUser("");
        setAppealsGroupBy(0);
        setSelectedTags([]);
        setReportsData(null);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`,
            "Group By": "Site"
        });
        setShowFilterPills(true);
        fetchAppealsReport(defaultRange.firstDate, defaultRange.lastDate, "", 0, []);
    }

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Date Range": {
                const defaultRange = dateRange("lastMonth")
                setFromDate(defaultRange.firstDate)
                setToDate(defaultRange.lastDate)
                setDatePreset(reportDatePresetByLabel["Last month"])
                fetchAppealsReport(defaultRange.firstDate, defaultRange.lastDate, selectedUser, appealsGroupBy, selectedTags)
                break
            }
            case "User":
                setSelectedUser("")
                fetchAppealsReport(fromDate, toDate, "", appealsGroupBy, selectedTags)
                break
            case "Group By":
                setAppealsGroupBy(0)
                fetchAppealsReport(fromDate, toDate, selectedUser, 0, selectedTags)
                break
            case "Tags":
                setSelectedTags([])
                fetchAppealsReport(fromDate, toDate, selectedUser, appealsGroupBy, [])
                break
            default:
                break
        }
    }

    const handleFilterPillClose = closedLabel => {
        clearFilterByLabel(closedLabel)
        setAppliedFilters(prevState => {
            const updatedFilters = { ...prevState }
            if (closedLabel === "Date Range") {
                const defaultRange = dateRange("lastMonth")
                updatedFilters["Date Range"] = `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
            } else if (closedLabel === "Group By") {
                updatedFilters["Group By"] = "Site"
            } else {
                updatedFilters[closedLabel] = null
            }
            return appliedFiltersUtilities.hasRemainingFilters(updatedFilters) ? updatedFilters : null
        })
    }

    const handleFilterPillItemRemove = (label, remainingIds) => {
        setAppliedFilters(prevState => appliedFiltersUtilities.keepMultiSelectionItems(prevState, label, remainingIds))
        removeMultiSelectFilterItemByLabel(label, remainingIds)
    }

    const removeMultiSelectFilterItemByLabel = (label, remainingIds) => {
        switch (label) {
            case "Tags":
                setSelectedTags(remainingIds)
                fetchAppealsReport(fromDate, toDate, selectedUser, appealsGroupBy, remainingIds)
                break
            default:
                break
        }
    }

    const fetchAppealsReport = (from, to, user, groupBy, tagIds) => {
        setIsLoading(true)
        Api.getAppealsReports(
            data => {
                setReportsData(data)
                setColumnTotals({
                    totalReceived: data.reduce((acc, curr) => {
                        acc += curr.appealsReceived;
                        return acc
                    }, 0),
                    totalAccepted: data.reduce((acc, curr) => {
                        acc += curr.appealsAccepted;
                        return acc
                    }, 0),
                    totalDeclined: data.reduce((acc, curr) => {
                        acc += curr.appealsDeclined;
                        return acc
                    }, 0),
                })
                setIsLoading(false)
            },
            error => {
                console.log(error);
                setIsLoading(false)
            },
            from,
            to,
            0,
            !stringHelpers.isNullOrEmpty(user) ? user : null,
            groupBy,
            tagIds,
            token,
            organisationId
        )
    }

    const handleDownload = () => {
        Api.getAppealsReports(
            (blob, filename) => { saveAs(blob, filename) },
            error => {
                console.log(error);
                setIsLoading(false)
            },
            fromDate,
            toDate,
            1,
            !stringHelpers.isNullOrEmpty(selectedUser) ? selectedUser : null,
            appealsGroupBy,
            selectedTags,
            token,
            organisationId
        )
    }


    const scheduleDateParameters = datePreset !== null
        ? { reportDatePreset: datePreset }
        : { from: fromDate, to: toDate }

    const scheduleInitialParameters = {
        ...scheduleDateParameters,
        userId: selectedUser,
        reportGroupBy: appealsGroupBy,
        tagIds: selectedTags,
    }

    const closeScheduleExportModal = () => setShowScheduleExportModal(false)
    useEffect(() => {
        fetchAppealsReport(fromDate, toDate, selectedUser, appealsGroupBy, selectedTags);
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(fromDate)) } - ${ toDateString(new Date(toDate)) }`,
            "Group By": groupByLabel(appealsGroupBy)
        });
        setShowFilterPills(true);
    }, [])

    useEffect(() => {
        if (!arrayUtilities.isNullOrEmpty(reportsData)) {
            setChartData(reportsData.map(item => (
                {
                    "label": appealsGroupBy === 1 ? item.tagName : item.siteName,
                    "Appeals Received": item.appealsReceived,
                    "Appeals Accepted": item.appealsAccepted,
                    "Appeals Declined": item.appealsDeclined
                }
            )))
        }
    }, [reportsData])

    const isTagGrouping = appealsGroupBy === 1
    const groupingColumnHeading = isTagGrouping ? "Tag" : "Site Name"

    const selectedTagNames = !arrayUtilities.isNullOrEmpty(selectedTags) && !arrayUtilities.isNullOrEmpty(tags)
        ? selectedTags
            .map(selectedTagId => tags.find(tag => tag.id === selectedTagId))
            .filter(tag => tag !== undefined)
            .map(tag => tag.name)
            .join(", ")
        : "All"

    return <>
    <div className="container-fluid">
        <div className="row">
            <div className="col-12">
                <div className="card">
                    <div className="card-header">
                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <h5 className="mb-0">
                                    Appeals
                                </h5>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="d-flex mr-2 align-items-center">
                                    <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                </div>
                                <button
                                    onClick={ () => handleDownload() }
                                    className="btn btn-sm btn-default text-white bg-dark nowrap mr-2"
                                >
                                    Download CSV<i className="ml-2 fas fa-file-excel"></i>
                                </button>
                                <button
                                    onClick={ () => downloadReportPdf(pdfRef, `${ organisationName } - Appeals`) }
                                    className="btn btn-sm btn-default nowrap mr-2 text-white" style={ { backgroundColor: "#4A7C8E" } }
                                >
                                    Download PDF<i className="ml-2 far fa-file-pdf"></i>
                                </button>
                                <button
                                    onClick={ () => setShowScheduleExportModal(true) }
                                    className="btn btn-sm btn-secondary nowrap mr-2"
                                >
                                    Schedule Report<i className="ml-2 far fa-clock"></i>
                                </button>
                                <Link
                                    className="btn btn-falcon-default btn-sm mr-3"
                                    type="button"
                                    to={ `/organisation/${ organisationId }/reports` }>
                                    <i className="fas fa-reply"></i> Back
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="card-body border-top d-flex flex-column">
        {
            showFilterPills && appliedFilters &&
            <div className="d-flex align-items-center flex-wrap">
                {
                    Object.entries(appliedFilters).map(
                        ([label, value]) => {
                            const resettable = label === "Date Range" || label === "Group By"
                            return <AppliedFilterPill
                                key={ label }
                                label={ label }
                                value={ value }
                                isResettable={ resettable }
                                renderItemLabel={ renderTagFilterItemLabel }
                                onTextClick={ () => setShowFiltersModal(true) }
                                onCloseClick={ handleFilterPillClose }
                                onRemoveItem={ handleFilterPillItemRemove }
                            />
                        }
                    )
                }
                { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => handleResetFilters() }>Reset all filters</button> }
            </div>
        }
        <div ref={ pdfRef }>
            <PrintPdfHeader organisationLogo={ organisationLogo } organisationName={ organisationName } title="Appeals">
                <div className="print-only-filter">
                    <span className="print-only-filter-label">Date Range</span>
                    <span className="print-only-filter-value">
                        { toDateString(fromDate) } - { toDateString(toDate) }
                    </span>
                </div>
                <div className="print-only-filter">
                    <span className="print-only-filter-label">User</span>
                    <span className="print-only-filter-value">
                        { pdfUserName(selectedUser) }
                    </span>
                </div>
                <div className="print-only-filter">
                    <span className="print-only-filter-label">Group By</span>
                    <span className="print-only-filter-value">
                        { groupByLabel(appealsGroupBy) }
                    </span>
                </div>
                <div className="print-only-filter">
                    <span className="print-only-filter-label">Tags</span>
                    <span className="print-only-filter-value">
                        { selectedTagNames }
                    </span>
                </div>
            </PrintPdfHeader>
            {
                isLoading
                    ? <Loader />
                    : <>
                        {
                            !arrayUtilities.isNullOrEmpty(reportsData)
                                ? (
                                    <div className="mt-3 d-flex flex-column">
                                        {
                                            chartData &&
                                            <ReportsCharts showLegend data={ chartData } chartStyle="bar" isGroupedChart />
                                        }
                                        <div className="card-body bg-white border-top p-0">
                                            <table className="table table-sm mb-0 table-striped table-dashboard fs--1 data-table border-bottom border-200 no-footer ">
                                                <thead className="bg-200 text-900">
                                                    <tr>
                                                        <th>{ groupingColumnHeading }</th>
                                                        <th className="text-right">Appeals Received</th>
                                                        <th className="text-right">Appeals Accepted</th>
                                                        <th className="text-right">Appeals Declined</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        !arrayUtilities.isNullOrEmpty(reportsData) && reportsData.map((item, index) =>
                                                            <tr key={ index }>
                                                                <td>
                                                                    {
                                                                        isTagGrouping
                                                                            ? item.tagName
                                                                            : <Link
                                                                                to={ { pathname: `/organisation/${ organisationId }/enforcement`, state: { siteId: item.siteId, status: 200 } } }>
                                                                                <h6 className="my-0">{ item.siteName }</h6>
                                                                            </Link>
                                                                    }
                                                                </td>
                                                                <td className="text-right">{ item.appealsReceived }</td>
                                                                <td className="text-right">{ item.appealsAccepted }</td>
                                                                <td className="text-right">{ item.appealsDeclined }</td>
                                                            </tr>)
                                                    }
                                                    {
                                                        columnTotals && (
                                                            <tr className="font-weight-bold w-100">
                                                                <td>Totals</td>
                                                                {
                                                                    Object.values(columnTotals)
                                                                        .map((value) => <td className="text-right">{ stringHelpers.formatDecimal(value, 0) }</td>)
                                                                }
                                                            </tr>
                                                        )
                                                    }
                                                </tbody>
                                            </table>
                                        </div>

                                    </div>
                                ) : <EmptyState message="No Data available for the selected filters" />
                        }
                    </>
            }
        </div>
        {
            showScheduleExportModal &&
            <AppealsScheduleModal
                users={ users }
                tags={ tags || [] }
                organisationId={ organisationId }
                token={ token }
                initialParameters={ scheduleInitialParameters }
                onCancel={ closeScheduleExportModal }
                onSaved={ closeScheduleExportModal }
            />
        }
                    </div>
                </div>
            </div>
        </div>
    </div>
    {
        showFiltersModal &&
        <AppealsReportFiltersModal
            showModal={ showFiltersModal }
            handleClose={ () => setShowFiltersModal(false) }
            handleConfirm={ (draftFilters) => {
                setFromDate(draftFilters.fromDate)
                setToDate(draftFilters.toDate)
                setDatePreset(draftFilters.datePreset)
                setSelectedUser(draftFilters.selectedUser)
                setAppealsGroupBy(draftFilters.appealsGroupBy)
                setSelectedTags(draftFilters.selectedTags)
                setAppliedFilters(draftFilters.appliedFilters)
                setShowFilterPills(true)
                setShowFiltersModal(false)
                fetchAppealsReport(draftFilters.fromDate, draftFilters.toDate, draftFilters.selectedUser, draftFilters.appealsGroupBy, draftFilters.selectedTags)
            } }
            initialFromDate={ fromDate }
            initialToDate={ toDate }
            initialDatePreset={ datePreset }
            initialSelectedUser={ selectedUser }
            initialAppealsGroupBy={ appealsGroupBy }
            initialSelectedTags={ selectedTags }
            initialAppliedFilters={ appliedFilters }
            users={ users }
            tags={ tags || [] }
            key="appeals-report-filters-modal"
        />
    }
    </>
}
